import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, AlertCircle, CheckCircle2, Building2 } from 'lucide-react';
import { projectsAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';
import { getStatusLabel, getStatusBadgeClass } from '../../utils/statusHelpers';

export default function ProjectsListPage() {
  const { isInternalTeam, user } = useAuthStore();
  const isOrgAdmin = user?.role === 'org_admin';
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [queueView, setQueueView] = useState('all'); // all, awaiting_action, at_risk, overdue, completed
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('25');

  const { data, isLoading } = useQuery({
    queryKey: ['projects', { status: statusFilter }],
    queryFn: () => projectsAPI.getAll({ status: statusFilter || undefined }),
    select: (res) => res.data,
  });

  const normalizeStatus = (status) => {
    const s = (status || '').toString().trim().toLowerCase();
    const allowed = new Set([
      'active', 'in_progress', 'in_review', 'completed', 'on_hold', 'archived', 'canceled',
    ]);
    return allowed.has(s) ? s : 'active';
  };

  // Queue categorization
  const awaitingActionStatuses = ['active', 'in_progress', 'in_review'];
  const completedStatuses = ['completed', 'archived', 'canceled'];
  const slaThresholdDays = 30;

  const deadlineString = new Date(Date.now() + slaThresholdDays * 24 * 60 * 60 * 1000).toLocaleDateString();
  const definitions = {
    awaiting_action: 'Projects that need action (ready, in progress, or in review)',
    at_risk: `Projects approaching the ${slaThresholdDays}-day SLA deadline${deadlineString ? ` (${deadlineString})` : ''}`,
    overdue: `Projects past the SLA deadline${deadlineString ? ` (${deadlineString})` : ''}`,
    completed: 'Projects that have finished or been archived/canceled',
    all: 'All projects',
  };

  const getSlaBucket = (project) => {
    if (completedStatuses.includes(normalizeStatus(project.status))) return 'completed';
    const lastChange = project.lastStatusChange || project.updatedAt;
    if (!lastChange) return 'compliant';
    const daysInStatus = Math.round((new Date() - new Date(lastChange)) / (1000 * 60 * 60 * 24));
    if (daysInStatus <= slaThresholdDays * 0.7) return 'compliant';
    if (daysInStatus <= slaThresholdDays) return 'atrisk';
    return 'overdue';
  };

  // Derive facility options for org_admin filter (from returned projects)
  const facilityOptions = useMemo(() => {
    if (!isOrgAdmin) return [];
    const seen = new Map();
    for (const p of data?.projects || []) {
      if (p.facilityId?._id) {
        seen.set(String(p.facilityId._id), p.facilityId.name);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data?.projects, isOrgAdmin]);

  const filteredProjects = useMemo(() => {
    let filtered = data?.projects || [];

    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(s) ||
        p.clientId?.name?.toLowerCase().includes(s) ||
        p.serviceCategory?.toLowerCase().includes(s) ||
        p.facilityId?.name?.toLowerCase().includes(s)
      );
    }

    // Facility filter (org_admin only)
    if (isOrgAdmin && facilityFilter) {
      if (facilityFilter === '__hq__') {
        filtered = filtered.filter(p => !p.facilityId);
      } else {
        filtered = filtered.filter(p => String(p.facilityId?._id) === facilityFilter);
      }
    }

    if (queueView === 'awaiting_action') {
      filtered = filtered.filter(p => awaitingActionStatuses.includes(normalizeStatus(p.status)));
    } else if (queueView === 'at_risk') {
      filtered = filtered.filter(p => getSlaBucket(p) === 'atrisk');
    } else if (queueView === 'overdue') {
      filtered = filtered.filter(p => getSlaBucket(p) === 'overdue');
    } else if (queueView === 'completed') {
      filtered = filtered.filter(p => completedStatuses.includes(normalizeStatus(p.status)));
    }

    return filtered;
  }, [data?.projects, search, facilityFilter, queueView, isOrgAdmin]);

  const statusCounts = useMemo(() => {
    const projects = data?.projects || [];
    const activeForSLA = projects.filter(p => !completedStatuses.includes(normalizeStatus(p.status)));
    return {
      all: projects.length,
      awaiting_action: projects.filter(p => awaitingActionStatuses.includes(normalizeStatus(p.status))).length,
      at_risk: activeForSLA.filter(p => getSlaBucket(p) === 'atrisk').length,
      overdue: activeForSLA.filter(p => getSlaBucket(p) === 'overdue').length,
      completed: projects.filter(p => completedStatuses.includes(normalizeStatus(p.status))).length,
    };
  }, [data?.projects]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, facilityFilter, queueView, pageSize]);

  const pagination = useMemo(() => {
    const total = filteredProjects.length;
    if (pageSize === 'all') return { total, pages: 1, currentPage: 1, items: filteredProjects };
    const limit = Math.max(1, Number(pageSize) || 25);
    const pages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.min(page, pages);
    const start = (currentPage - 1) * limit;
    return { total, pages, currentPage, items: filteredProjects.slice(start, start + limit) };
  }, [filteredProjects, page, pageSize]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">Projects</h1>
          <p className="mt-1 text-secondary-600">
            Manage and track all your projects{deadlineString ? ` – ${slaThresholdDays}-day SLA; new items due by ${deadlineString}` : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
            <input
              type="text"
              placeholder="Search projects by name, client, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All Statuses</option>
            <optgroup label="Active">
              <option value="active">Ready</option>
              <option value="in_progress">In Progress</option>
            </optgroup>
            <optgroup label="Review & Completion">
              <option value="in_review">In Review</option>
              <option value="completed">Completed</option>
            </optgroup>
            <optgroup label="Other">
              <option value="on_hold">On Hold</option>
              <option value="archived">Archived</option>
              <option value="canceled">Canceled</option>
            </optgroup>
          </select>
          {isOrgAdmin && facilityOptions.length > 0 && (
            <select
              value={facilityFilter}
              onChange={(e) => setFacilityFilter(e.target.value)}
              className="input w-full sm:w-52"
            >
              <option value="">All Facilities</option>
              <option value="__hq__">HQ (Org-level)</option>
              {facilityOptions.map(({ id, name }) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Queue Tabs */}
      <div className="card border-b border-secondary-200">
        <div className="flex overflow-x-auto gap-1 p-4">
          {[
            { id: 'all', label: 'All', icon: null },
            { id: 'awaiting_action', label: 'Awaiting Action', icon: AlertCircle },
            { id: 'at_risk', label: 'At Risk', icon: AlertCircle },
            { id: 'overdue', label: 'Overdue', icon: AlertCircle },
            { id: 'completed', label: 'Completed', icon: CheckCircle2 },
          ].map(({ id, label, icon: Icon }) => {
            const isOverdueAlert = id === 'overdue' && statusCounts.overdue > 0;
            const isActive = queueView === id;
            return (
              <button
                key={id}
                title={definitions[id]}
                onClick={() => setQueueView(id)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : `bg-secondary-100 hover:bg-secondary-200 ${isOverdueAlert ? 'text-red-600' : 'text-secondary-700'}`
                }`}
              >
                {Icon && <Icon size={18} />}
                {label}
                <span className={`text-sm font-semibold ${isActive ? 'text-white' : isOverdueAlert ? 'text-red-600' : 'text-secondary-600'}`}>
                  {statusCounts[id]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Projects list */}
      <div className="card">
        <div className="flex items-center justify-between px-6 py-3 border-b border-secondary-200">
          <p className="text-sm text-secondary-600">Showing {pagination.items.length} of {pagination.total} projects</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary-600">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Show all</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredProjects.length > 0 ? (
          <div className="divide-y divide-secondary-200">
            {pagination.items.map((project) => (
              <Link
                key={project._id}
                to={`/projects/${project._id}`}
                className="block p-6 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-secondary-900 truncate">
                      {project.name}
                    </h3>
                    <p className="mt-1 text-sm text-secondary-500">
                      {project.clientId?.name} • {project.serviceCategory}
                    </p>
                    {isOrgAdmin && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-secondary-400">
                        <Building2 size={12} />
                        {project.facilityId?.name ?? 'HQ'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={getStatusBadgeClass(project.status)}>
                      {getStatusLabel(project.status)}
                    </span>
                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        navigate(`/projects/${project._id}?requestChange=1`);
                      }}
                      className="btn-outline btn-sm"
                    >
                      Request Change
                    </button>
                  </div>
                </div>
                {project.description && (
                  <p className="mt-2 text-sm text-secondary-600 line-clamp-2">
                    {project.description}
                  </p>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-secondary-500">
            No projects found matching your criteria
          </div>
        )}
        {filteredProjects.length > 0 && pagination.pages > 1 && pageSize !== 'all' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={pagination.currentPage === 1}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-secondary-600">Page {pagination.currentPage} of {pagination.pages}</span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(pagination.pages, prev + 1))}
              disabled={pagination.currentPage === pagination.pages}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
